import * as _ from 'lodash';

import { ProjectCategory } from './common';
import type { ProjectSortPrefix, ProjectsSort } from './sorting';

/**
 * Icon constants - these should match the imports from the components
 * We'll define the mapping here to avoid circular dependencies
 */
export const SORT_ICON_MAP = {
  ascending: 'arrowUp',
  descending: 'arrowDown',
  unsorted: 'anglesUpDown',
} as const;

/**
 * API Endpoints used for projects v2 queries.
 */
export const ENDPOINTS = {
  PROJECTS: '/api/v2/projects',
  RELEASE_GROUPS: '/api/v2/release-groups',
  CATEGORIES_SUMMARY: '/api/v2/projects/summary',
  ORGANIZATION_LABELS: '/api/organizations/labels',
  TEAMS: '/api/teams',
  BULK_LABEL: '/api/v2/projects/labels',
};

/**
 * is the passed in category "projects"
 */
export function isProjects(category: ProjectCategory): boolean {
  return category === ProjectCategory.Projects;
}

/**
 * is the passed in category "releaseGroups"
 */
export function isReleaseGroups(category: ProjectCategory): boolean {
  return category === ProjectCategory.ReleaseGroups;
}

/**
 * is the passed in category "sharedProjects"
 */
export function isSharedProjects(category: ProjectCategory): boolean {
  return category === ProjectCategory.SharedProjects;
}

/**
 * does the passed in "sort" start with "category"
 */
export function isSortSelected(sort: ProjectsSort, category: ProjectSortPrefix) {
  return sort.startsWith(category);
}

/**
 * does the passed in "sort" end with "direction"
 */
export function isSortedByDirection(sort: ProjectsSort, direction: 'asc' | 'desc') {
  return sort.endsWith(direction);
}

/**
 * Returns the appropriate sort icon type for a column header based on current sort state
 * @param sort - Current sort string from Redux store
 * @param columnPrefixes - Array of prefixes this column can be sorted by (e.g., ['title'] or ['issues-total'])
 */
export function getSortIconType(
  sort: ProjectsSort,
  columnPrefixes: ProjectSortPrefix | ProjectSortPrefix[],
): 'unsorted' | 'ascending' | 'descending' {
  const prefixes = _.castArray(columnPrefixes);

  // Check if any of the column's prefixes are currently selected
  const isColumnSorted = prefixes.some(prefix => isSortSelected(sort, prefix));

  if (!isColumnSorted) {
    return 'unsorted';
  }

  return isSortedByDirection(sort, 'desc') ? 'descending' : 'ascending';
}

/**
 * Returns the appropriate icon name for a column header based on current sort state
 * @param sort - Current sort string from Redux store
 * @param columnPrefixes - Array of prefixes this column can be sorted by
 * @returns The icon name to use (matches icon constants from components)
 */
export function getSortIconName(
  sort: ProjectsSort,
  columnPrefixes: ProjectSortPrefix | ProjectSortPrefix[],
): string {
  const iconType = getSortIconType(sort, columnPrefixes);
  return SORT_ICON_MAP[iconType];
}

/**
 * Check if any of the provided sort prefixes are currently selected
 * @param sort - Current sort string from Redux store
 * @param prefixes - Array of prefixes to check
 * @returns True if any of the prefixes are selected
 */
export function isAnySortSelected(
  sort: ProjectsSort,
  prefixes: ProjectSortPrefix[],
): boolean {
  return prefixes.some(prefix => isSortSelected(sort, prefix));
}
